import {
  useCallback,
  useEffect,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import { Box, TextField } from '@mui/material';

type OtpCodeInputProps = {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  /** numeric = digits only; text = alphanumeric (backup codes) */
  mode?: 'numeric' | 'text';
  autoFocus?: boolean;
};

const sanitize = (raw: string, mode: 'numeric' | 'text', length: number) => {
  const cleaned =
    mode === 'numeric'
      ? raw.replace(/\D/g, '')
      : raw.replace(/[^a-zA-Z0-9]/g, '');
  return cleaned.slice(0, length).toUpperCase();
};

const OtpCodeInput = ({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
  mode = 'numeric',
  autoFocus = true,
}: OtpCodeInputProps) => {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const completedRef = useRef<string | null>(null);

  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  useEffect(() => {
    if (autoFocus && !disabled) {
      inputsRef.current[0]?.focus();
    }
  }, [autoFocus, disabled, length]);

  useEffect(() => {
    if (value.length === length && value !== completedRef.current) {
      completedRef.current = value;
      onComplete?.(value);
    }
    if (value.length < length) {
      completedRef.current = null;
    }
  }, [value, length, onComplete]);

  const replaceAt = useCallback(
    (index: number, char: string) => {
      const chars = Array.from({ length }, (_, i) => value[i] ?? '');
      chars[index] = char;
      const joined = sanitize(chars.join(''), mode, length);
      onChange(joined);
      return joined;
    },
    [length, mode, onChange, value]
  );

  const focusIndex = (index: number) => {
    const el = inputsRef.current[Math.max(0, Math.min(length - 1, index))];
    el?.focus();
    el?.select();
  };

  const handleChange = (index: number, raw: string) => {
    if (disabled) return;
    const cleaned = sanitize(raw, mode, length);
    if (!cleaned) {
      replaceAt(index, '');
      return;
    }
    // Paste or multi-char into one box
    if (cleaned.length > 1) {
      const merged = sanitize(
        value.slice(0, index) + cleaned + value.slice(index + cleaned.length),
        mode,
        length
      );
      onChange(merged);
      focusIndex(Math.min(length - 1, index + cleaned.length));
      return;
    }
    replaceAt(index, cleaned);
    if (index < length - 1) {
      focusIndex(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[index]) {
        replaceAt(index, '');
      } else if (index > 0) {
        replaceAt(index - 1, '');
        focusIndex(index - 1);
      }
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusIndex(index - 1);
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      focusIndex(index + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent) => {
    if (disabled) return;
    e.preventDefault();
    const pasted = sanitize(e.clipboardData.getData('text'), mode, length);
    if (!pasted) return;
    onChange(pasted);
    focusIndex(Math.min(length - 1, pasted.length));
  };

  return (
    <Box
      role="group"
      aria-label="Ellenőrző kód"
      onPaste={handlePaste}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: { xs: 0.75, sm: 1 },
      }}
    >
      {digits.map((digit, index) => (
        <TextField
          key={index}
          value={digit}
          disabled={disabled}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={(e) => e.target.select()}
          inputRef={(el) => {
            inputsRef.current[index] = el;
          }}
          inputProps={{
            inputMode: mode === 'numeric' ? 'numeric' : 'text',
            autoComplete: index === 0 ? 'one-time-code' : 'off',
            maxLength: length,
            'aria-label': `${index + 1}. számjegy`,
            style: {
              textAlign: 'center',
              fontSize: '1.25rem',
              fontWeight: 600,
              padding: '12px 0',
            },
          }}
          sx={{
            width: { xs: 44, sm: 48 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
            },
          }}
        />
      ))}
    </Box>
  );
};

export default OtpCodeInput;
